import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Lock, LogOut, Trash2, Mail, RefreshCw, Image as ImageIcon,
  Inbox, Plus, Upload, ExternalLink, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api, setAdminToken, getAdminToken, adminAuthHeader } from "@/lib/api";

// =====================
// Login screen
// =====================
function AdminLogin({ onLogin }) {
  const [mode, setMode] = useState("email"); // "email" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "email") {
        const res = await api.post(
          "/admin/email-login",
          { email, password },
          { withCredentials: true }
        );
        // Backend sets httpOnly cookie + returns user; we also store token for legacy bearer fallback
        if (res.data?.session_token) setAdminToken(res.data.session_token);
        toast.success("Welcome back.");
        onLogin();
      } else {
        const res = await api.post("/admin/login", { password });
        setAdminToken(res.data.token);
        toast.success("Welcome back.");
        onLogin();
      }
      setPassword("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid credentials";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 relative">
      <div className="grid-bg absolute inset-0 radial-mask opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md tracing-card p-10" data-testid="admin-login">
        <div className="flex justify-center mb-8">
          <Logo size="md" />
        </div>
        <div className="eyebrow mb-3 text-center">// Admin Access</div>
        <h1 className="font-display text-2xl font-semibold text-slate-900 text-center">
          Authentication Required
        </h1>

        {/* Mode toggle */}
        <div className="mt-6 flex border border-slate-200 p-1 gap-1">
          <button
            type="button"
            onClick={() => setMode("email")}
            className={`flex-1 text-xs font-mono uppercase tracking-[0.15em] py-2 transition-colors ${
              mode === "email" ? "bg-[#0066FF] text-white" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="admin-mode-email"
          >
            Email + Password
          </button>
          <button
            type="button"
            onClick={() => setMode("password")}
            className={`flex-1 text-xs font-mono uppercase tracking-[0.15em] py-2 transition-colors ${
              mode === "password" ? "bg-[#0066FF] text-white" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="admin-mode-password"
          >
            Password Only
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "email" && (
            <div className="relative">
              <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@email.com"
                className="w-full bg-white border border-slate-300 pl-10 pr-4 py-3 text-sm text-slate-900 focus-ring outline-none"
                data-testid="admin-email-input"
              />
            </div>
          )}
          <div className="relative">
            <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "email" ? "Password" : "Admin password"}
              className="w-full bg-white border border-slate-300 pl-10 pr-4 py-3 text-sm text-slate-900 focus-ring outline-none"
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full justify-center disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-[#0066FF]">
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}

// =====================
// Submissions panel
// =====================
function SubmissionsPanel() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/submissions", { headers: adminAuthHeader() });
      setSubmissions(res.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/submissions/${id}`, { headers: adminAuthHeader() });
      setSubmissions((s) => s.filter((x) => x.id !== id));
      if (active && active.id === id) setActive(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">// Inbox</div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">
            Contact Submissions
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {submissions.length} total · sorted by newest
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-ghost text-xs font-mono uppercase tracking-[0.15em]"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="tracing-card divide-y divide-slate-100">
            {submissions.length === 0 && !loading && (
              <div className="p-10 text-center text-sm text-slate-500">No submissions yet.</div>
            )}
            {submissions.map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s)}
                className={`w-full text-left p-5 hover:bg-slate-50 transition-colors ${
                  active && active.id === s.id ? "bg-blue-50/50 border-l-2 border-[#0066FF]" : ""
                }`}
                data-testid={`admin-submission-${s.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-base font-semibold text-slate-900 truncate">
                        {s.name}
                      </span>
                      <span className="text-[0.6rem] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border border-[#0066FF]/40 text-[#0066FF]">
                        {s.submission_kind}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{s.email}</div>
                    <div className="mt-2 text-sm text-slate-600 truncate">
                      {s.project_type} {s.domain ? `· ${s.domain}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-slate-400">
                      {new Date(s.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-[0.65rem] font-mono text-slate-400 mt-1">
                      {new Date(s.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="tracing-card p-6 sticky top-24">
            {!active ? (
              <div className="text-center text-sm text-slate-500 py-10">
                Select a submission to view details.
              </div>
            ) : (
              <div data-testid="admin-submission-detail">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-display text-xl font-semibold text-slate-900">{active.name}</div>
                    {active.company && (
                      <div className="text-sm text-slate-600 mt-1">{active.company}</div>
                    )}
                  </div>
                  <button
                    onClick={() => remove(active.id)}
                    className="text-slate-400 hover:text-red-500 transition-colors p-2"
                    data-testid="admin-delete-btn"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="space-y-3 text-sm border-y border-slate-200 py-4">
                  <DetailRow label="Type" value={active.project_type} />
                  {active.domain && <DetailRow label="Domain" value={active.domain} mono blue />}
                  <DetailRow
                    label="Email"
                    value={<a href={`mailto:${active.email}`} className="text-[#0066FF] hover:underline">{active.email}</a>}
                  />
                  <DetailRow label="Received" value={new Date(active.created_at).toLocaleString()} small />
                </div>

                <div className="mt-5">
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mb-2">
                    Message
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {active.message}
                  </div>
                </div>

                <a
                  href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.project_type)}`}
                  className="mt-6 btn-primary w-full justify-center text-sm"
                  data-testid="admin-reply-btn"
                >
                  <Mail size={14} /> Reply via Email
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono, blue, small }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 font-mono text-xs uppercase tracking-[0.15em]">{label}</span>
      <span
        className={`text-right ${mono ? "font-mono text-xs" : ""} ${blue ? "text-[#0066FF]" : "text-slate-900"} ${small ? "text-xs" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

// =====================
// Banners panel
// =====================
function BannersPanel() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Upload form state
  const [title, setTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [order, setOrder] = useState(0);
  const [imageBase64, setImageBase64] = useState("");
  const [fileName, setFileName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/banners", { headers: adminAuthHeader() });
      setBanners(res.data);
    } catch {
      toast.error("Failed to load banners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image must be under 4MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageBase64(ev.target.result);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setTitle("");
    setLinkUrl("");
    setOrder(0);
    setImageBase64("");
    setFileName("");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!imageBase64) {
      toast.error("Please select an image.");
      return;
    }
    setCreating(true);
    try {
      await api.post(
        "/admin/banners",
        { title, link_url: linkUrl, order: Number(order) || 0, image_base64: imageBase64, active: true },
        { headers: adminAuthHeader() }
      );
      toast.success("Banner uploaded.");
      reset();
      load();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.patch(
        `/admin/banners/${b.id}`,
        { active: !b.active },
        { headers: adminAuthHeader() }
      );
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const updateOrder = async (b, newOrder) => {
    try {
      await api.patch(
        `/admin/banners/${b.id}`,
        { order: Number(newOrder) || 0 },
        { headers: adminAuthHeader() }
      );
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.delete(`/admin/banners/${id}`, { headers: adminAuthHeader() });
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">// Web Banners</div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">
            Homepage Banner Carousel
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {banners.length} banner{banners.length !== 1 ? "s" : ""} · displays on Home above hero. Lower order shows first.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-ghost text-xs font-mono uppercase tracking-[0.15em]"
          data-testid="admin-banners-refresh-btn"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Upload form */}
        <div className="lg:col-span-5">
          <form onSubmit={create} className="tracing-card p-6 space-y-4" data-testid="banner-upload-form">
            <div className="eyebrow">// Add New Banner</div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Image *</label>
              <label
                htmlFor="banner-file-input"
                className="mt-2 cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-[#0066FF] transition-colors p-6 bg-slate-50"
              >
                {imageBase64 ? (
                  <img src={imageBase64} alt="preview" className="max-h-32 object-contain" />
                ) : (
                  <>
                    <Upload size={24} className="text-slate-400 mb-2" />
                    <span className="text-sm text-slate-700">Click to upload image</span>
                    <span className="text-xs text-slate-500 mt-1">PNG, JPG · max 4MB · landscape recommended</span>
                  </>
                )}
              </label>
              <input
                id="banner-file-input"
                type="file"
                accept="image/*"
                onChange={onFile}
                className="hidden"
                data-testid="banner-file-input"
              />
              {fileName && (
                <p className="mt-2 text-xs text-slate-500 font-mono">{fileName}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Title (optional)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. New SaaS Platform Launch"
                className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none"
                data-testid="banner-title-input"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Link URL (optional)</label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="/services  or  https://..."
                className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none"
                data-testid="banner-link-input"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Display Order</label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none"
                data-testid="banner-order-input"
              />
            </div>
            <button
              type="submit"
              disabled={creating || !imageBase64}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="banner-create-btn"
            >
              <Plus size={14} /> {creating ? "Uploading..." : "Add Banner"}
            </button>
          </form>
        </div>

        {/* List */}
        <div className="lg:col-span-7 space-y-4">
          {banners.length === 0 && !loading && (
            <div className="tracing-card p-10 text-center text-sm text-slate-500">
              No banners yet. Upload your first one →
            </div>
          )}
          {banners.map((b) => (
            <div key={b.id} className="tracing-card overflow-hidden" data-testid={`banner-item-${b.id}`}>
              <div className="relative h-32 bg-slate-100 overflow-hidden">
                <img src={b.image_base64} alt={b.title} className="w-full h-full object-cover" />
                {!b.active && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                    <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-700 bg-white px-3 py-1 border border-slate-300">
                      Hidden
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm font-semibold text-slate-900 truncate">
                    {b.title || <span className="text-slate-400 italic">Untitled</span>}
                  </div>
                  {b.link_url && (
                    <a
                      href={b.link_url}
                      target={b.link_url.startsWith("http") ? "_blank" : undefined}
                      rel="noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-[#0066FF] hover:underline truncate"
                    >
                      {b.link_url} <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={b.order}
                    onChange={(e) => updateOrder(b, e.target.value)}
                    className="w-14 bg-white border border-slate-300 px-2 py-1.5 text-xs text-slate-900 focus-ring outline-none text-center"
                    data-testid={`banner-order-${b.id}`}
                    title="Display order"
                  />
                  <button
                    onClick={() => toggleActive(b)}
                    className="p-2 text-slate-500 hover:text-[#0066FF] transition-colors"
                    title={b.active ? "Hide from site" : "Show on site"}
                    data-testid={`banner-toggle-${b.id}`}
                  >
                    {b.active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    onClick={() => remove(b.id)}
                    className="p-2 text-slate-500 hover:text-red-500 transition-colors"
                    data-testid={`banner-delete-${b.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================
// Main Admin component
// =====================
const TABS = [
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "banners", label: "Banners", icon: ImageIcon },
];

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [tab, setTab] = useState("submissions");

  const logout = () => {
    setAdminToken(null);
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between">
          <Logo size="sm" />
          <button
            onClick={logout}
            className="btn-secondary text-xs py-2 px-4"
            data-testid="admin-logout-btn"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 -mt-px flex items-center gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? "text-[#0066FF] border-[#0066FF]"
                    : "text-slate-600 border-transparent hover:text-slate-900"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {tab === "submissions" && <SubmissionsPanel />}
        {tab === "banners" && <BannersPanel />}
      </div>
    </div>
  );
}
